<!-- The input that is used for checkboxes and radiobuttons -->
<template>
    <div
        :data-outerfield="outerfield"
        :data-disabled="(disabled || state.global.showLoader).toString()"
        data-checkboxradio
        @click.self="wrapperClick"
    >
        <!-- The actual checkbox or radiobutton, which is invisible -->
        <input
            :type="type"
            :id="id"
            :disabled="disabled || state.global.showLoader"
            :tabindex="tabindex"
            :checked="answerData.isChecked"
            :name="name === `` ? false : name"
            @click="inputClick"
            @keyup.space.prevent="inputSpaceUp"
            data-only-screenreader
            :value="answerData.label"
        />
        <!-- The visual part of the checkbox or radiobutton -->
        <label
            :for="id"
            :role="type"
            @keydown.space.prevent
            :aria-checked="answerData.isChecked.toString()"
            tabindex="-1"
            data-visual-aid
            :id="id.replace(`checkradio-answer`, `checkradio-answer-label`)"
        >
            <font-awesome-icon v-if="type === `checkbox`" icon="check" aria-hidden="true" />
        </label>
        <!-- The text that accompanies the checkbox or radio -->
        <label
            v-if="showLabel"
            :for="id"
            :disabled="disabled || state.global.showLoader"
            data-input-text
            :id="`ownlabel-${id}`"
            v-html="DOMPurify.sanitize(answerData.label)"
        ></label>
    </div>
</template>

<script>
export default {
    name: `checkbox-radio`,
    inject: ['DOMPurify'],
    props: {
        questionData: {
            type: Object,
        },
        // Is this checkbox an outerfield?
        outerfield: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: `checkbox`,
        },
        answerData: {
            type: Object,
        },
        sqData: {
            type: Object,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        showLabel: {
            type: Boolean,
            default: false,
        },
        name: {
            type: String,
            default: ``,
        },
        tabindex: {
            type: Number,
            default: 0,
        },
    },
    computed: {
        state: function () {
            return this.$store.state;
        },
        id: function () {
            if (this.questionData.subQuestions) {
                return `question-${this.questionData.number}_sq-${this.sqData.number}_checkradio-answer-${this.answerData.number}`;
            } else return `question-${this.questionData.number}_checkradio-answer-${this.answerData.number}`;
        },
    },
    methods: {
        wrapperClick: function (e) {
            if (this.disabled || this.state.global.showLoader) return;

            this.$emit(`element-click`, e);
        },
        inputClick: function (e) {
            this.$emit(`element-click`, e);
        },
        inputSpaceUp: function (e) {
            // Click instead of data updates because then it will go through the usual checks in the corresponding event.
            this.$emit(`element-click`, e);
        },
    },
};
</script>

<style scoped>
@import "./../styles/variables.css";

[data-checkboxradio] {
    display: flex;
    align-items: center;
    position: relative;
    padding: 16px 0 16px 20px;

    &[data-disabled="false"] {
        cursor: pointer;
    }

    &[data-disabled="true"] {
        cursor: default;
    }

    [data-input-text] {
        margin-left: 10px;
        width: 100%;
        min-width: 10%;

        &[disabled] {
            color: #909090;
            pointer-events: none;
        }
    }
}

[data-outerfield] {
    width: 100%;
    margin-top: 10px;
    justify-content: flex-start;
    padding: 16px 0 0 0;
    [data-input-text] {
        margin-inline-start: 20px;
    }
}

input[type="checkbox"],
input[type="radio"] {
    width: 21px;
    height: 21px;
    cursor: pointer;

    &:focus-visible + [data-visual-aid] {
        outline: 2px solid #1A1A1A;
        outline-offset: 2px;
    }
}

input[type="radio"] {
    & + label {
        width: 21px;
        height: 21px;
        border: 1.31px solid var(--primary-color);
        background: #fff;
        border-radius: 50%;
        padding: 10px;
        position: relative;
        transition: 0.4s ease-out;
        flex-shrink: 0;

        &:before {
            content: "";
            border-radius: 50%;
            position: absolute;
            width: 0;
            height: 0;
            top: 50%;
            left: 50%;
            transition: 0.2s;
            transition-delay: 0.1s;
        }
    }

    &:not([disabled]) + label {
        &:active:after {
            width: calc(100% + 20px);
            height: calc(100% + 20px);
            top: -15px;
            left: -15px;
            background: var(--primary-color);
            opacity: 0.2;
        }

        &:after {
            content: "";
            position: absolute;
            width: 0;
            height: 0;
            top: 50%;
            left: 50%;
            bottom: 50%;
            right: 50%;
            border-radius: 50%;
            transition: 0.2s;
            transition-delay: 0.1s;
        }
    }

    &[disabled] + label,
    &:checked[disabled] + label {
        border-color: #e6e6e6;
        background: #f8f8f8;
        cursor: default;
        pointer-events: none;

        &:before {
            background: #e6e6e6;
        }
    }

    &[disabled] label {
        color: #909090;
        cursor: default;
        pointer-events: none;
    }

    &:checked + label,
    &[data-is-checked="true"] + label {
        
        &:before {
            background: var(--primary-color);
            width: 60%;
            height: 60%;
            top: 20%;
            bottom: 20%;
            left: 20%;
            right: 20%;
            border-radius: 50%;
        }
    }
}

input[type="checkbox"] {
    & + label {
        width: 21px;
        height: 21px;
        border: 1.31px solid var(--primary-color);
        padding: 10px;
        position: relative;
        transition: 0.4s ease-out;
        border-radius: 5px;
        flex-shrink: 0;

        svg {
            opacity: 0;
            color: #454848;
            position: absolute;
            top: 6%;
            left: 15%;
            font-size: 16px;
            transition: 0.2s;
            path {
                fill: #ffffff;
            }
        }
        &:checked + label,
        &[data-is-checked="true"] + label
        &:hover {
            background: #0000000D;
        }
    }

    /* &:checked + label{
        background: var(--primary-color);
    } */

    &[disabled] + label,
    &:checked[disabled] + label {
        border-color: var(--border-black-color-25);
        background: #E5E5E5;
        cursor: default;
        pointer-events: none;

        svg path {
            fill: #909090;
        }
    }

    &:not([disabled]) + label {
        &:after {
            content: "";
            border-radius: 50%;
            position: absolute;
            width: 0;
            height: 0;
            top: 50%;
            left: 50%;
            transition: 0.2s;
            transition-delay: 0.1s;
        }

        &:active:after {
            width: calc(100% + 20px);
            height: calc(100% + 20px);
            top: -15px;
            left: -15px;
            background: var(--primary-color);
            opacity: 0.2;
        }
    }

    &:checked + label,
    &[data-is-checked="true"] + label {
        background: var(--primary-color);
        border: 1.31px solid var(--primary-color);

        svg {
            opacity: 1;
            font-size: 16px;
            transform: rotate(5deg);
        }
    }
}

@media (forced-colors: active) {
    input[type="radio"] {
        & + label {
            border-color: CanvasText;
            background: Canvas;
        }

        &:checked + label:before,
        &[data-is-checked="true"] + label:before {
            /* Preserve the system-colored selection dot against background forcing. */
            forced-color-adjust: none;
            background: CanvasText;
        }

        &[disabled] + label,
        &:checked[disabled] + label {
            border-color: GrayText;

            &:before {
                background: GrayText;
            }
        }

        &:focus-visible + [data-visual-aid] {
            outline-color: Highlight;
        }
    }
}

@media only screen and (max-width: 550px) {
    input[type="checkbox"],
    input[type="radio"] {
        width: 40px;
        height: 40px;
    }
    input[type="checkbox"],
    input[type="radio"] {
        & + label {
            width: 40px;
            height: 40px;
            svg {
                top: 26%;
                left: 31%;
            }
        }
    }

    input[type="checkbox"] {
        label svg {
            font-size: 23px;
        }
    }
}
</style>